import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { useParams } from "react-router-dom"
import { Box, Grid, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { StandardQRCode } from "../../../../../../shared/components/qrCode/StandardQRCode"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { ExternalLink } from "../../../../../../shared/components/links/ExternalLink"
import { FormikSwitch } from "../../../../../../shared/components/forms/Formik"
import { queryClient } from "../../../../../../core/api/queryClient"
import { ShopDetailsResponse, ShopsQuery } from "../../shopsApi"
import { CreateOrEditShopFormValues } from "../../shopsTypes"
import { InfoBox } from "./InfoBox"

const useStyles = makeStyles()((theme) => ({
    infoWrap: {
        display: "grid",
        alignItems: "center",
        gridTemplateColumns: "1rem 1fr",
        background: theme.palette.tailwind.sky[50],
        borderRadius: "0.5rem",
        gap: "0.625rem",
        padding: "0.75rem",
        color: theme.palette.tailwind.sky[700],
        width: "100%",
        "&> svg": {
            display: "block",
        },
    },
    cardContentLink: {
        color: theme.palette.tailwind.sky[500],
        textDecoration: "none",
        transition: "all ease-in-out 0.3s",
        "&:hover": {
            color: `${theme.palette.tailwind.sky[800]}`,
        },
    },
    box: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: "0.5rem",
        gap: "1.25rem",
        padding: "1.5rem",
        textAlign: "center",
    },
    switchLabelWrap: {
        "& label.MuiFormControlLabel-root": {
            margin: 0,
            justifyContent: "flex-start",
        },
    },

    offContent: {
        textAlign: "center",
        color: theme.palette.tailwind.slate[400],
        fontStyle: "italic",
    },
}))

export const PublicFormContent = () => {
    const { classes } = useStyles()
    const { shopId } = useParams()
    const { values } = useFormikContext<CreateOrEditShopFormValues>()
    const shopDetails = values?.shopDetails

    const isShop = queryClient.getQueryData([ShopsQuery.DETAILS, { shopId }]) as ShopDetailsResponse

    return (
        <>
            <LocalizedStrict id="merchant-shop-form-make-shop-public-info" attrs={{ description: true }}>
                <InfoBox description="A public shop is accessible to everyone." />
            </LocalizedStrict>
            <LocalizedStrict id="merchant-shop-form-make-shop-public-title">
                <Typography variant="subtitle2">Share the following with your customers</Typography>
            </LocalizedStrict>
            <LocalizedStrict
                id="merchant-shop-form-make-shop-public-description"
                elems={{
                    externalLink: (
                        <ExternalLink href={"/"} className={classes.cardContentLink}>
                            here
                        </ExternalLink>
                    ),
                }}
            >
                <Typography variant="body2">
                    To pay as a guest, you must either scan a QR code or enter the shop number to access the shop. Read
                    more about public shops <ExternalLink href={"/"}>here</ExternalLink>.
                </Typography>
            </LocalizedStrict>
            <Stack direction="column" alignItems="center" className={classes.box}>
                {isShop?.shop ? (
                    <>
                        <LocalizedStrict id="merchant-shop-form-make-shop-public-number">
                            <Typography variant="subtitle2">Shop number:</Typography>
                        </LocalizedStrict>
                        <Typography variant="h4">{shopDetails.publicShopDetails?.shopNumber}</Typography>
                        <Grid container alignItems="center" justifyContent="center">
                            <Typography variant="subtitle2">
                                <LocalizedStrict id="merchant-shop-form-make-shop-public-url">
                                    <>Direct public url:</>
                                </LocalizedStrict>
                                <ExternalLink
                                    href={shopDetails.publicShopDetails?.shopUrl}
                                    className={classes.cardContentLink}
                                >
                                    {shopDetails.publicShopDetails?.shopUrl}
                                </ExternalLink>
                            </Typography>
                        </Grid>
                        <LocalizedStrict id="merchant-shop-form-make-shop-public-qr-code">
                            <Typography variant="subtitle2">
                                - or users can scan the following QR with their mobile phone
                            </Typography>
                        </LocalizedStrict>
                        <StandardQRCode size={190} url={shopDetails.publicShopDetails?.shopQR || ""} />
                    </>
                ) : (
                    <LocalizedStrict id="merchant-shop-form-make-shop-public-offContent">
                        <Typography className={classes.offContent}>Save to see sharing details.</Typography>
                    </LocalizedStrict>
                )}
            </Stack>

            <Box className={classes.switchLabelWrap}>
                <LocalizedStrict id="merchant-shop-form-field-isVisibleForAppUsers">
                    <FormikSwitch name="shopDetails.publicShopDetails.isVisibleForAppUsers" labelPlacement="end">
                        Make shop visible for internal users in GoPay mobile app
                    </FormikSwitch>
                </LocalizedStrict>
            </Box>
        </>
    )
}
